import { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import { Bold, Italic, AlignLeft, AlignCenter, AlignRight, Wand2, Eraser } from "lucide-react";
import { LineHeight, FontSize, FontFamily, FONT_SIZES, FONT_FAMILIES, LINE_HEIGHTS } from "./RichTextEditor";
import { HEAD_VARS, LETTER_CSS, renderHeadTemplate, buildLetterInnerHtml, emailHeaderHtml } from "../lib/letterPdf";

const DEFAULT_TPL = '<p style="text-align: left; line-height: 1.15"><strong><span style="font-size: 14px">{{studio.nome}}</span></strong></p>'
  + '<p style="text-align: left; line-height: 1.15"><span style="font-size: 12px">{{studio.indirizzo}} - {{studio.cap}} {{studio.comune}} ({{studio.provincia}})</span></p>'
  + '<p style="text-align: left; line-height: 1.15"><span style="font-size: 12px">P.iva {{studio.partita_iva}} - Tel. {{studio.telefono}} - Cell. {{studio.cellulare}}</span></p>'
  + '<p style="text-align: left; line-height: 1.15"><span style="font-size: 12px">E-mail: {{studio.email}} - PEC: {{studio.pec}}</span></p>';

const Btn = ({ active, onClick, children, testid, title }) => (
  <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick} data-testid={testid}
    className={`h-8 w-8 inline-flex items-center justify-center rounded transition-colors ${active ? "bg-amber-500 text-slate-950" : "text-slate-300 hover:bg-slate-800"}`}>
    {children}
  </button>
);

function HeadPreview({ html, studio }) {
  const [mode, setMode] = useState("lettera");
  const inte = { html: renderHeadTemplate(html, studio) };
  const out = mode === "lettera"
    ? `<style>${LETTER_CSS}</style><div class="pgp-pdfroot">${buildLetterInnerHtml({ contentHtml: "<p>Gentile destinatario, testo della lettera…</p>", withHead: true, inte })}</div>`
    : emailHeaderHtml(inte) + '<div style="font-family:Arial,sans-serif;font-size:14px;max-width:624px;margin:0 auto">Testo del corpo email…</div>';
  return (
    <div className="mt-3" data-testid="intestazione-preview">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-xs text-slate-400">Anteprima in diretta</span>
        {["lettera", "email"].map((m) => (
          <button key={m} type="button" onClick={() => setMode(m)} data-testid={`intestazione-preview-${m}`}
            className={`text-xs px-2 py-0.5 rounded transition-colors ${mode === m ? "bg-amber-500 text-slate-950" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
            {m === "lettera" ? "Lettera / PDF" : "Email"}
          </button>
        ))}
      </div>
      <div className="bg-white rounded-md p-6 overflow-x-auto">
        <div style={{ width: 624, maxWidth: "100%", margin: "0 auto" }} dangerouslySetInnerHTML={{ __html: out }} />
      </div>
    </div>
  );
}

export function IntestazioneEditor({ value, onChange, studio }) {
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: false, bulletList: false, orderedList: false, blockquote: false, codeBlock: false }),
      TextStyle, Color, FontSize, FontFamily, LineHeight, TextAlign.configure({ types: ["paragraph"] })],
    content: value || "",
    onUpdate: ({ editor: ed }) => onChange(ed.isEmpty ? "" : ed.getHTML()),
  });

  useEffect(() => {
    if (editor && !editor.isFocused && (value || "") !== (editor.isEmpty ? "" : editor.getHTML())) editor.commands.setContent(value || "", false);
  }, [value, editor]);

  if (!editor) return null;
  const ts = editor.getAttributes("textStyle");
  const lh = editor.getAttributes("paragraph").lineHeight || "1.15";
  const setLh = (v) => editor.chain().focus().updateAttributes("paragraph", { lineHeight: v }).run();
  const sel = "h-8 rounded bg-slate-900/60 border border-slate-700 text-slate-200 text-xs px-1.5";

  return (
    <div data-testid="intestazione-editor">
      <div className="flex flex-wrap items-center gap-1 rounded-t-md border border-slate-700 bg-slate-900/80 p-1.5">
        <select className={sel} value={ts.fontFamily || ""} onChange={(e) => editor.chain().focus().setMark("textStyle", { fontFamily: e.target.value || null }).run()} data-testid="intestazione-font">
          <option value="">Font</option>
          {FONT_FAMILIES.map((f) => <option key={f.l} value={f.v}>{f.l}</option>)}
        </select>
        <select className={sel} value={ts.fontSize || ""} onChange={(e) => editor.chain().focus().setMark("textStyle", { fontSize: e.target.value || null }).run()} data-testid="intestazione-size">
          <option value="">Dim.</option>
          {FONT_SIZES.map((s) => <option key={s} value={s}>{s.replace("px", "")}</option>)}
        </select>
        <Btn title="Grassetto" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} testid="intestazione-bold"><Bold className="h-4 w-4" /></Btn>
        <Btn title="Corsivo" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} testid="intestazione-italic"><Italic className="h-4 w-4" /></Btn>
        <input type="color" title="Colore testo" value={ts.color || "#111827"} onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          className="h-8 w-9 rounded border border-slate-700 bg-transparent cursor-pointer" data-testid="intestazione-color" />
        <Btn title="Sinistra" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()} testid="intestazione-align-left"><AlignLeft className="h-4 w-4" /></Btn>
        <Btn title="Centro" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()} testid="intestazione-align-center"><AlignCenter className="h-4 w-4" /></Btn>
        <Btn title="Destra" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()} testid="intestazione-align-right"><AlignRight className="h-4 w-4" /></Btn>
        <select className={sel} value={lh} onChange={(e) => setLh(e.target.value)} title="Interlinea" data-testid="intestazione-lineheight">
          {LINE_HEIGHTS.map((o) => <option key={o.v} value={o.v}>Interl. {o.l}</option>)}
        </select>
        <div className="ml-auto flex gap-1">
          <button type="button" onClick={() => editor.commands.setContent(DEFAULT_TPL, true)} data-testid="intestazione-default"
            className="h-8 px-2 inline-flex items-center gap-1 rounded text-xs text-slate-300 hover:bg-slate-800 transition-colors"><Wand2 className="h-3.5 w-3.5" /> Modello base</button>
          <button type="button" onClick={() => editor.commands.clearContent(true)} data-testid="intestazione-clear"
            className="h-8 px-2 inline-flex items-center gap-1 rounded text-xs text-slate-300 hover:bg-slate-800 transition-colors"><Eraser className="h-3.5 w-3.5" /> Svuota</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 border-x border-slate-700 bg-slate-900/40 px-2 py-1.5" data-testid="intestazione-vars">
        <span className="text-xs text-slate-500 self-center mr-1">Variabili:</span>
        {HEAD_VARS.map((v) => (
          <button key={v.key} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().insertContent(`{{studio.${v.key}}}`).run()}
            data-testid={`intestazione-var-${v.key}`}
            className="text-xs px-2 py-0.5 rounded-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/15 transition-colors">{v.label}</button>
        ))}
      </div>
      <div className="rounded-b-md border border-slate-700 bg-white text-slate-900 px-4 py-3 min-h-[110px] [&_.ProseMirror]:outline-none [&_.ProseMirror_p]:m-0" data-testid="intestazione-editor-content">
        <EditorContent editor={editor} />
      </div>
      <p className="text-xs text-slate-500 mt-1">Se lasci vuoto, nelle lettere e nelle email viene usata l'intestazione automatica. Vale per la sede principale.</p>
      <HeadPreview html={value} studio={studio} />
    </div>
  );
}
